/**
 * Obstruction Attachments Service
 * API operations for obstruction file attachments with upload progress.
 * Mirrors `attachmentsService` but targets the obstruction attachment routes.
 */
import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '@/constants/api';
import type { ObstructionAttachmentResponseDto } from '@/types/api/responses';
import type { UploadProgressCallback } from './attachmentsService';

export const obstructionAttachmentsService = {
  /**
   * Upload a file attachment to an obstruction.
   * Uses multipart/form-data for file upload with progress tracking.
   *
   * @param obstructionId - Obstruction ID to attach file to
   * @param file - File to upload
   * @param onProgress - Optional progress callback receiving percentage (0-100)
   * @returns Promise with the created attachment metadata
   */
  upload: (
    obstructionId: string,
    file: File,
    onProgress?: UploadProgressCallback
  ) => {
    const formData = new FormData();
    formData.append('file', file);

    return apiClient.post<ObstructionAttachmentResponseDto>(
      API_ENDPOINTS.OBSTRUCTION_ATTACHMENTS.BASE(obstructionId),
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percent);
          }
        },
      }
    );
  },

  /**
   * Same-origin URL of the download endpoint. Use with `<a href download>` so
   * the browser follows the 302 to the storage signed URL natively (no CORS).
   */
  getDownloadUrl: (attachmentId: string) =>
    API_ENDPOINTS.OBSTRUCTION_ATTACHMENTS.DOWNLOAD(attachmentId),

  /**
   * Same-origin streaming preview — needed by in-browser viewers that consume
   * raw bytes (PDF / DOCX / image). Bypasses the cross-origin redirect so axios
   * arraybuffer works without bucket CORS.
   */
  fetchPreview: (attachmentId: string) =>
    apiClient.get<ArrayBuffer>(
      API_ENDPOINTS.OBSTRUCTION_ATTACHMENTS.PREVIEW(attachmentId),
      {
        responseType: 'arraybuffer',
      }
    ),

  /**
   * Delete an attachment from an obstruction.
   *
   * @param obstructionId - Obstruction ID the attachment belongs to
   * @param attachmentId - Attachment ID to delete
   * @returns Promise that resolves on successful deletion
   */
  delete: (obstructionId: string, attachmentId: string) =>
    apiClient.delete(
      API_ENDPOINTS.OBSTRUCTION_ATTACHMENTS.DELETE(obstructionId, attachmentId)
    ),
};
